import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Ruler, Clock } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { TEAM, IMAGES } from "../data/siteData";

const VALUES = [
  {
    icon: Ruler,
    title: "Measured Craft",
    text: "Straight lines, consistent heights, clean edges. We measure twice and mow once — every visit, every property.",
  },
  {
    icon: Leaf,
    title: "Organic First",
    text: "Slow-release organic feeding and pet-safe products come standard. A beautiful yard shouldn't cost the ecosystem.",
  },
  {
    icon: Clock,
    title: "Relentlessly Reliable",
    text: "Confirmed windows, weather-adapted routing and same-day communication. If we say Tuesday, we mean Tuesday.",
  },
];

export default function About() {
  return (
    <main className="bg-[#FBF9F5] pt-28 lg:pt-36">
      <section className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Philosophy & Team</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-black tracking-tight text-[#1A2E20] sm:text-5xl lg:text-6xl">
              Rooted in craft since 2022.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#4a544c] sm:text-lg">
              Skloccom began with a secondhand mower, a stubborn streak and a founder who started landscaping at
              nineteen. Four years later the mower is newer, but the streak isn't: we still walk every property before
              we quote it, and we still don't leave until the edges are crisp.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-[#4a544c]">
              We keep the operation deliberately small — owner on-site, a tight crew, honest pricing — because the
              details that make a property look effortless only happen when somebody actually cares.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-t-[8rem] border border-[#234E33]/15">
              <img src={IMAGES.about} alt="Fresh turf installed by Skloccom" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#1A2E20] py-20 lg:py-24 mt-20 lg:mt-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="font-mono-x text-xs font-semibold uppercase tracking-[0.3em] text-[#72B043]">How We Work</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#FBF9F5] sm:text-4xl">
              Three rules, no exceptions
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="border-l-2 border-[#72B043]/50 pl-6">
                  <v.icon size={22} className="text-[#72B043]" />
                  <h3 className="mt-4 font-display text-xl font-bold text-[#FBF9F5]">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#FBF9F5]/65">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <Reveal>
          <Eyebrow>Meet the Team</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#1A2E20] sm:text-4xl">
            The people behind the stripes
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.1}>
              <article
                data-testid={`team-card-${m.name.toLowerCase().replace(/[^a-z]/g, "")}`}
                className="flex h-full flex-col border border-[#234E33]/12 bg-[#FAF7F2] p-8"
              >
                <img
                  src={m.avatar}
                  alt={`${m.name}, ${m.role} at Skloccom`}
                  className="h-24 w-24 rounded-full border-2 border-[#2E6941]/30 object-cover"
                />
                <h3 className="mt-5 font-display text-xl font-bold text-[#1A2E20]">{m.name}</h3>
                <p className="mt-1 font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C86D51]">{m.role}</p>
                <p className="mt-4 text-sm leading-relaxed text-[#4a544c]">{m.bio}</p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 text-center">
          <Link
            to="/contact"
            data-testid="about-quote-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1A2E20] px-8 py-4 text-sm font-semibold text-[#FBF9F5] transition-colors duration-200 hover:bg-[#2E6941]"
          >
            Work With Us
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
